import { z } from 'zod';

import type { BadgeTone } from '@/components/ui/Badge';
import { GENDERS, memberSchema } from '@/features/members';
import {
  dateOnlySchema,
  emailInputSchema,
  mobileInputSchema,
  optionalId,
  optionalInput,
  optionalText,
  staffRefSchema,
  timestampSchema,
} from '@/types/schemas';

// --- Enums (mirror prisma/schema.prisma in the backend) ---------------------------------

export const PROSPECT_STATUSES = ['new', 'contacted', 'follow_up', 'converted', 'lost'] as const;
export type ProspectStatus = (typeof PROSPECT_STATUSES)[number];

/** Statuses staff can set by hand — "converted" only comes from converting to a member. */
export const EDITABLE_STATUSES = ['new', 'contacted', 'follow_up', 'lost'] as const;

export const PROSPECT_STATUS_LABEL: Record<ProspectStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  follow_up: 'Follow-up',
  converted: 'Converted',
  lost: 'Lost',
};

export const PROSPECT_STATUS_TONE: Record<ProspectStatus, BadgeTone> = {
  new: 'info',
  contacted: 'accent',
  follow_up: 'warning',
  converted: 'success',
  lost: 'neutral',
};

export const PROSPECT_SOURCES = [
  'walk_in',
  'phone_call',
  'referral',
  'website',
  'social_media',
  'other',
] as const;
export type ProspectSource = (typeof PROSPECT_SOURCES)[number];

/** Most enquiries are someone walking in off the street, so a new one starts there. */
export const DEFAULT_PROSPECT_SOURCE: ProspectSource = 'walk_in';

export const PROSPECT_SOURCE_LABEL: Record<ProspectSource, string> = {
  walk_in: 'Walk-in',
  phone_call: 'Phone call',
  referral: 'Referral',
  website: 'Website',
  social_media: 'Social media',
  other: 'Other',
};

// --- API responses --------------------------------------------------------------------

export const prospectSchema = z.object({
  id: z.number(),
  name: z.string(),
  mobile: z.string(),
  email: z.string().nullable(),
  source: z.enum(PROSPECT_SOURCES).nullable(),
  interestedPackage: z.object({ id: z.number(), name: z.string() }).nullable(),
  notes: z.string().nullable(),
  followUpDate: dateOnlySchema.nullable(),
  status: z.enum(PROSPECT_STATUSES),
  convertedMemberId: z.number().nullable(),
  /** The staff member whose lead this is. Null for enquiries added before the field existed. */
  owner: staffRefSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Prospect = z.infer<typeof prospectSchema>;

export const conversionResultSchema = z.object({
  prospect: prospectSchema,
  member: memberSchema,
});
export type ConversionResult = z.infer<typeof conversionResultSchema>;

// --- Forms ------------------------------------------------------------------------------

export const prospectFormSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(150, 'Use at most 150 characters'),
  mobile: mobileInputSchema,
  email: optionalInput(emailInputSchema),
  source: z.union([z.literal('').transform(() => null), z.enum(PROSPECT_SOURCES)]),
  interestedPackageId: optionalId,
  /** Picked from the staff list; blank means nobody is credited. */
  ownerId: optionalId,
  followUpDate: optionalInput(dateOnlySchema),
  status: z.enum(EDITABLE_STATUSES),
  notes: optionalText(5000),
});
export type ProspectInput = z.output<typeof prospectFormSchema>;

/**
 * An enquiry keeps neither a date of birth nor a gender, and a member profile must have both
 * (same rule as `POST /members`), so conversion asks for them here.
 */
export const convertFormSchema = z.object({
  firstName: z.string().trim().min(1, 'Enter a first name').max(100),
  lastName: optionalText(100),
  mobile: mobileInputSchema,
  email: optionalInput(emailInputSchema),
  dateOfBirth: z.string().trim().min(1, 'Enter the date of birth').pipe(dateOnlySchema),
  gender: z.enum(GENDERS, { error: 'Choose a gender' }),
  emergencyContactName: optionalText(150),
  emergencyContactMobile: optionalInput(mobileInputSchema),
  // No member code: the API numbers the new member itself (M-00001).
});
